// Twilio Voice SDK error codes → one plain sentence for the setter. Codes verified against
// node_modules/@twilio/voice-sdk/es5/twilio/errors/generated.js (v2.18).

export interface PhoneErrorLike { code?: number; message?: string; name?: string }

export type PhoneErrorKind = 'mic' | 'token' | 'network' | 'busy' | 'auth' | 'other';

export interface PlainPhoneError { kind: PhoneErrorKind; message: string; code: number | null }

export function explainPhoneError(e: PhoneErrorLike | null | undefined): PlainPhoneError {
  const code = typeof e?.code === 'number' ? e.code : null;
  switch (code) {
    case 31401: // PermissionDeniedError
      return { kind: 'mic', code, message: 'Your microphone is blocked. Click the icon at the left of the address bar, allow the microphone, then try again.' };
    case 31402: // AcquisitionFailedError
      return { kind: 'mic', code, message: "Couldn't open your microphone. Check the headset is plugged in and not being used by another app." };
    case 20104: // AccessTokenExpired
    case 31205: // JWTTokenExpiredError
      return { kind: 'token', code, message: 'The phone login expired. Reconnecting the phone…' };
    case 20101: // AccessTokenInvalid
    case 31204: // InvalidJWTTokenError
      return { kind: 'token', code, message: 'The phone login was rejected. Reload the page; if it happens again, tell PJ.' };
    case 20151: // AuthenticationFailed
    case 31201: // AuthorizationError
      return { kind: 'auth', code, message: "Twilio refused this phone's login. Tell PJ; calling is paused until it's fixed." };
    case 31005: // ConnectionError
    case 31009: // TransportError
    case 53000: // Signaling connection error
    case 53405: // Media connection failed
      return { kind: 'network', code, message: 'Lost the connection to the phone service. Check your internet; the call may have dropped.' };
    case 31480: // TemporarilyUnavailable
    case 31486: // BusyHere
    case 31603: // Decline
      return { kind: 'busy', code, message: 'The line was busy or declined the call.' };
    case 31206: // RateExceededError
      return { kind: 'other', code, message: 'Too many phone requests at once. Wait a few seconds and try again.' };
    default:
      return { kind: 'other', code, message: `Phone error${code ? ` ${code}` : ''}. If it repeats, reload the page and tell PJ.` };
  }
}

/** Voice Insights warning names emitted by Call 'warning' (call.js WARNING_NAMES × WARNING_PREFIXES). */
const WARNING_TEXT: Record<string, string> = {
  'high-rtt': 'Network delay is high',
  'high-jitter': 'Audio is choppy (network)',
  'low-mos': 'Call quality is poor',
  'high-packet-loss': 'Audio is breaking up (network)',
  'high-packets-lost-fraction': 'Audio is breaking up (network)',
  'low-bytes-received': "You may not hear them (no audio arriving)",
  'low-bytes-sent': "They may not hear you (no audio leaving)",
  'constant-audio-input-level': "Your microphone isn't picking up sound",
  'ice-connectivity-lost': 'Connection lost; reconnecting',
};

export function explainWarning(name: string): string {
  return WARNING_TEXT[name] ?? `Call quality warning (${name})`;
}

/**
 * GET /api/voice/token failed. A "not configured" answer (TELEPHONY: 503 twilio_not_configured, whose message names
 * missing secrets and an admin tool) becomes one setter-facing sentence; the server's own text is kept as `detail`,
 * which only admins see. Anything else is a plain error the setter can retry.
 */
export interface VoiceTokenProblem { status: 'not_configured' | 'error'; message: string; detail: string | null }

export const CALLING_NOT_SET_UP = "Calling isn't set up yet. Tell PJ; there's nothing to fix on your side.";

export function voiceTokenProblem(httpStatus: number | null, error: string | null, serverMessage: string): VoiceTokenProblem {
  const notConfigured = (error !== null && /not_configured$/.test(error)) || httpStatus === 501;
  if (notConfigured) return { status: 'not_configured', message: CALLING_NOT_SET_UP, detail: serverMessage || null };
  return { status: 'error', message: `Couldn't get a phone login: ${serverMessage}`, detail: null };
}

/**
 * Toast for an incoming call that stopped ringing before this browser answered. Twilio cancels the other ring-group
 * invites when a teammate answers, and the SDK reports that exactly like the caller hanging up, so don't claim either.
 */
export function inboundEndedMessage(from: string): string {
  return `The call from ${from} stopped ringing: the caller hung up or a teammate answered. If they left a voicemail, it will be in Tasks.`;
}
